/**
 * Utility functions for attendee management with conditional client selection
 */

/**
 * Detect program type based on SO Client field
 * @param {Object} soRecord - Scheduled Offering record from Airtable
 * @returns {Object} Program type detection result
 */
export function detectProgramType(soRecord) {
  if (!soRecord || !soRecord.fields) {
    return {
      type: 'loading',
      clientData: null,
      participatingClients: []
    };
  }

  try {
    // Get Client field value - can be string, array, or null
    const clientField = soRecord.fields['Client'] || soRecord.fields['Client Name'] || null;
    
    // Normalize to string for comparison (handle both string and array formats)
    let clientValue = '';
    if (Array.isArray(clientField)) {
      clientValue = clientField.join(', ').toUpperCase();
    } else if (typeof clientField === 'string') {
      clientValue = clientField.toUpperCase();
    }

    // Check for open program indicator (case-insensitive)
    const isOpenProgram = clientValue.includes('OPEN-PROGRAM');

    if (isOpenProgram) {
      // Open program - get participating clients
      const participatingClients = soRecord.fields['Participating Clients'] || [];
      
      return {
        type: 'open',
        clientData: null, // No fixed client for open programs
        participatingClients: Array.isArray(participatingClients) ? participatingClients : []
      };
    } else {
      // Regular program - extract client data
      const clientData = clientField ? {
        id: Array.isArray(clientField) ? clientField[0] : clientField,
        name: soRecord.fields['Client Name'] || clientField
      } : null;

      return {
        type: 'regular',
        clientData,
        participatingClients: []
      };
    }
  } catch (error) {
    console.error('Error detecting program type:', error);
    return {
      type: 'loading',
      clientData: null,
      participatingClients: []
    };
  }
}

/**
 * Validate client selection based on program type and requirements
 * @param {string} programType - 'open' or 'regular'
 * @param {Object|null} selectedClient - Selected client object
 * @param {Array} participatingClients - Array of participating client objects
 * @returns {Object} Validation result with error messaging
 */
export function validateClientSelection(programType, selectedClient, participatingClients) {
  try {
    // Regular programs are always valid (client is auto-assigned from SO)
    if (programType === 'regular') {
      return {
        isValid: true,
        error: null
      };
    }

    // Open program validation
    if (programType === 'open') {
      // Check if participating clients are available
      if (!participatingClients || participatingClients.length === 0) {
        return {
          isValid: false,
          error: 'Ask Ops to add the "Participating Clients"'
        };
      }

      // Check if client is selected
      if (!selectedClient) {
        return {
          isValid: false,
          error: 'Please select a company'
        };
      }

      // Validate that selected client is in the participating clients list
      const isClientInList = participatingClients.some(client => {
        if (!client || !client.id) return false;
        return client.id === selectedClient.id;
      });

      if (!isClientInList) {
        return {
          isValid: false,
          error: 'Selected client is not eligible for this program'
        };
      }

      // All validations passed
      return {
        isValid: true,
        error: null
      };
    }

    // Unknown program type
    return {
      isValid: false,
      error: 'Unable to validate client selection - unknown program type'
    };

  } catch (error) {
    console.error('Error validating client selection:', error);
    return {
      isValid: false,
      error: 'Validation error - please try again'
    };
  }
}

/**
 * Handle errors in client data operations with appropriate user messaging
 * @param {string} errorType - Type of error encountered
 * @param {Object} context - Additional context for error handling
 * @returns {Object} Error handling configuration
 */
export function handleClientError(errorType, context = {}) {
  const errorTypes = {
    EMPTY_PARTICIPATING_CLIENTS: {
      severity: 'warning',
      message: 'Ask Ops to add the "Participating Clients"',
      action: 'block_submission',
      userGuidance: 'Contact operations team to configure participating clients for this open program'
    },
    
    MISSING_SO_CLIENT: {
      severity: 'warning',
      message: 'Client information missing for this program',
      action: 'allow_with_warning',
      userGuidance: 'Attendee will be created without client assignment - operations will need to assign later'
    },
    
    CLIENT_FETCH_FAILED: {
      severity: 'error',
      message: 'Unable to load client information',
      action: 'graceful_degradation',
      userGuidance: 'Some features may be limited. Please refresh the page and try again'
    },
    
    INVALID_CLIENT_SELECTION: {
      severity: 'error',
      message: 'Selected client is not eligible for this program',
      action: 'block_submission',
      userGuidance: 'Please select from the available companies in the dropdown'
    },

    CLIENT_VALIDATION_ERROR: {
      severity: 'error',
      message: 'Client validation failed',
      action: 'block_submission',
      userGuidance: 'Please check your selection and try again'
    },

    UNKNOWN_ERROR: {
      severity: 'error',
      message: 'An unexpected error occurred',
      action: 'retry',
      userGuidance: 'Please try again or contact support if the issue persists'
    }
  };

  const errorConfig = errorTypes[errorType] || errorTypes.UNKNOWN_ERROR;

  // Add context information if provided
  let enhancedMessage = errorConfig.message;
  if (context.programType === 'open' && errorType === 'EMPTY_PARTICIPATING_CLIENTS') {
    enhancedMessage = `This open program needs participating clients configured. ${errorConfig.message}`;
  }
  
  if (context.soNumber && (errorType === 'MISSING_SO_CLIENT' || errorType === 'EMPTY_PARTICIPATING_CLIENTS')) {
    enhancedMessage += ` (SO ${context.soNumber})`;
  }

  return {
    ...errorConfig,
    message: enhancedMessage,
    context: {
      ...context,
      errorType,
      timestamp: new Date().toISOString()
    }
  };
}

/**
 * Format client data for dropdown display
 * @param {Array} clients - Array of client records from Airtable
 * @returns {Array} Formatted client options for dropdown
 */
export function formatClientsForDropdown(clients) {
  if (!Array.isArray(clients)) {
    console.warn('formatClientsForDropdown: Expected array, received:', typeof clients);
    return [];
  }

  try {
    return clients
      .filter(client => {
        // Filter out invalid client records
        if (!client || typeof client !== 'object') {
          return false;
        }

        // Ensure we have required fields
        const hasId = client.id || client.recordId;
        const hasName = (client.fields && (client.fields['Client Long Name'] || client.fields['ClientID'])) ||
                      client.name ||
                      client.clientName;

        return hasId && hasName;
      })
      .map(client => {
        // Extract client information from various possible structures
        let id, name, clientId;

        if (client.fields) {
          // Airtable record format
          id = client.id || client.recordId;
          name = client.fields['Client Long Name'] || client.fields['ClientID'] || 'Unknown Client';
          clientId = client.fields['ClientID'] || id;
        } else {
          // Already formatted or simplified format
          id = client.id || client.recordId;
          name = client.name || client.clientName || client['Client Long Name'] || 'Unknown Client';
          clientId = client.clientId || client.ClientID || id;
        }

        // Return shadcn/ui compatible format
        return {
          id: id,
          name: name,
          value: id, // Value used by form controls
          label: name, // Display text
          clientId: clientId // For additional reference
        };
      })
      .sort((a, b) => {
        // Sort alphabetically by name
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });

  } catch (error) {
    console.error('Error formatting clients for dropdown:', error);
    return [];
  }
}